import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { AxiosError } from 'axios';

import { UrlEnvironmentType } from '@/typings/url';
import { API_V3, ApiV3Response, Environment, getSomethingPaginated } from './types';

export const getEnvironment = async (
  accountId: number,
  environmentId: number,
  includeRelated: string[] = ['jobs'],
): Promise<Environment> => {
  const response = await axios.get<ApiV3Response<Environment>>(
    `${API_V3}/accounts/${accountId}/environments/${environmentId}/`,
    {
      params: {
        include_related: includeRelated.length ? `[${includeRelated.join(',')}]` : '[]',
      },
    },
  );

  return response?.data?.data;
};

export const getProductionEnvironments = async (
  accountId: number,
  projectIds: number[],
): Promise<Environment[]> => {
  return await getSomethingPaginated<Environment>(
    API_V3,
    `/accounts/${accountId}/environments/`,
    {
      deployment_type: 'production',
      include_related: '[]',
      project_id__in: JSON.stringify(projectIds),
    },
  );
};

export const getAccountProductionAndStagingEnvironments = async (
  accountId: number,
): Promise<Environment[]> => {
  const [data, devData] = await Promise.all([
    getSomethingPaginated<Environment>(API_V3, `/accounts/${accountId}/environments/`, {
      deployment_type__in: JSON.stringify(['staging', 'production']),
      include_related: '[]',
    }),
    getSomethingPaginated<Environment>(API_V3, `/accounts/${accountId}/environments/`, {
      type: 'development',
      include_related: '[]',
    }),
  ]);
  return [...data, ...devData];
};

export interface DevStagingProdEnvsResponse {
  production?: Environment;
  staging?: Environment;
  development?: Environment;
}

type DevStagingProdProps = {
  accountId: number | undefined;
  projectId: number | undefined;
};

export const getDevStagingProdEnvs = async ({
  accountId,
  projectId,
}: DevStagingProdProps) => {
  const [data, developmentData] = await Promise.all([
    getSomethingPaginated<Environment>(
      API_V3,
      `/accounts/${accountId}/projects/${projectId}/environments/`,
      {
        deployment_type__in: JSON.stringify(['staging', 'production']),
        include_related: '[]',
      },
    ),
    getSomethingPaginated<Environment>(
      API_V3,
      `/accounts/${accountId}/projects/${projectId}/environments/`,
      {
        type: 'development',
        include_related: '[]',
      },
    ),
  ]);

  return {
    production: data.find(
      (env) => env.project_id === projectId && env.deployment_type === 'production',
    ),
    staging: data.find(
      (env) => env.project_id === projectId && env.deployment_type === 'staging',
    ),
    development: developmentData.find(
      (env) => env.project_id === projectId && env.type === 'development',
    ),
  } satisfies DevStagingProdEnvsResponse;
};

export const useAdminEnvironment = (
  environmentId?: number | string,
  accountId?: number,
  includeRelated?: string[],
  preventUpdate: boolean = false,
  enabled: boolean = true,
) => {
  return useQuery<Environment, AxiosError>({
    queryKey: ['environment', accountId, environmentId, includeRelated],
    queryFn: () =>
      getEnvironment(accountId ?? 0, Number(environmentId) ?? 0, includeRelated),
    enabled: !!(accountId && environmentId && environmentId !== 'new') && enabled,
    refetchOnWindowFocus: !preventUpdate,
    staleTime: preventUpdate ? Infinity : undefined,
    refetchOnMount: !preventUpdate,
    refetchOnReconnect: !preventUpdate,
  });
};

export const useAdminProductionEnvironments = (
  accountId: number | undefined,
  projectIds: number[],
) => {
  return useQuery<Environment[], AxiosError>({
    queryKey: ['environments', accountId, projectIds],
    queryFn: () => getProductionEnvironments(accountId!, projectIds),
    enabled: !!accountId && !!projectIds.length,
    refetchOnWindowFocus: false,
  });
};

export const useProdEnvQuery = (
  accountId: number | undefined,
  projectId: number | undefined,
) => {
  const projectIds = useMemo(() => (projectId ? [projectId] : []), [projectId]);
  const query = useAdminProductionEnvironments(accountId, projectIds);
  return { ...query, data: query.data?.[0] ?? null };
};

export const useAdminDevStagingProdEnvs = ({
  accountId,
  projectId,
}: DevStagingProdProps) => {
  return useQuery<DevStagingProdEnvsResponse, AxiosError>({
    queryKey: ['environments', accountId, projectId],
    queryFn: () =>
      getDevStagingProdEnvs({
        accountId,
        projectId,
      }),
    enabled: !!accountId && !!projectId,
  });
};

/**
 * This is identical to useAdminDevStagingProdEnvs, with a different queryKey value.
 * Using useAdminDevStagingProdEnvs in `CopilotDrawer` was causing an infinite render loop.
 */
export const useEnvsForCopilot = ({ accountId, projectId }: DevStagingProdProps) => {
  return useQuery<DevStagingProdEnvsResponse, AxiosError>({
    queryKey: ['environments-for-copilot', accountId, projectId],
    queryFn: () =>
      getDevStagingProdEnvs({
        accountId,
        projectId,
      }),
    enabled: !!accountId && !!projectId,
  });
};

export const useEnvironmentMap = (
  accountId: number | undefined,
): Record<number, UrlEnvironmentType> => {
  const allEnvironments = useQuery<Environment[], AxiosError>({
    queryKey: ['multipleEnvironments', accountId],
    queryFn: () => getAccountProductionAndStagingEnvironments(accountId!),
    enabled: !!accountId,
  }).data;
  const environmentMap = useMemo(() => {
    return (
      allEnvironments?.reduce(
        (acc: Record<number, UrlEnvironmentType>, { id, deployment_type, type }) => {
          if (!id || (!deployment_type && !type)) {
            return acc;
          }
          if (type === 'development') {
            acc[id] = 'development';
            return acc;
          } else if (
            deployment_type === 'production' ||
            deployment_type === 'staging'
          ) {
            acc[id] = deployment_type as UrlEnvironmentType;
            return acc;
          }
          return acc;
        },
        {},
      ) || {}
    );
  }, [allEnvironments]);
  return environmentMap;
};
